import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#4f46e5';
  const lastUpdated = 'July 30, 2026';

  return (
    <LegalLayout app="Verdict" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
      <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlabs ("we," "our," or "us") operates the Verdict mobile application (the "App"). Verdict is a decision journal: write down what you decided, why, and what you expect to happen — then come back later, record how it actually went, and learn your patterns.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use the App. We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                Verdict is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local-First Storage:</strong> Your decisions, predictions, reviews, and insights are stored locally on your device.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration, login, or any form of account creation.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics, behavioral data, or any form of telemetry.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Third-Party Tracking:</strong> We do not integrate any third-party analytics, tracking, or advertising SDKs.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We Store
              </h2>
              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Decision &amp; Review Data
              </h3>
              <p className="mb-4 leading-relaxed">
                Verdict stores the journal you build, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Decisions with category, reasoning, counter-thesis, expected outcome, confidence level, and alternatives considered</li>
                <li>Review dates and outcome ratings (better, as expected, or worse)</li>
                <li>Insights: outcome distributions, category patterns, and your calibration score</li>
              </ul>
              <p className="leading-relaxed">
                Important: Your reasoning and reflections are personal writing. They are stored only on your device and are never transmitted anywhere.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage &amp; Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data is protected by multiple layers of security:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage:</strong> All data is stored in the App's private on-device storage, protected by iOS's built-in encryption.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Remote Servers:</strong> Your decision journal are never uploaded to servers owned or operated by us.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>iOS Sandbox:</strong> The App operates within iOS's security sandbox, preventing other apps from accessing your data.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                Verdict does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>The content of your decisions on any server</li>
                <li>Your device location</li>
                <li>Contact information</li>
                <li>Photos or media files</li>
                <li>Browsing history</li>
                <li>Device identifiers for tracking purposes</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Network Connections
              </h2>
              <p className="leading-relaxed">
                Verdict makes no network requests. The App has no backend, calls no external APIs, and works entirely offline. Nothing you enter ever leaves your device unless you explicitly export or share it yourself.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Notifications
              </h2>
              <p className="leading-relaxed">
                If you grant notification permission, Verdict schedules local reminders for decision review dates you set. All notifications are scheduled on-device through iOS; we never send push notifications from servers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Subscriptions &amp; In-App Purchases
              </h2>
              <p className="mb-4 leading-relaxed">
                Verdict offers an optional Verdict Pro purchase (monthly or yearly subscription, or a one-time lifetime unlock). All purchases are processed entirely by the Apple App Store. We do not process, store, or have access to your payment information.
              </p>
              <p className="leading-relaxed">
                Entitlement is verified through Apple's StoreKit framework on your device. No purchase or billing data is ever sent to servers of ours — we do not operate any.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                Verdict uses Apple's native frameworks only: SwiftUI, UserNotifications, and StoreKit. We do not integrate any third-party analytics tools, crash reporters, or advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights &amp; Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>View &amp; Edit:</strong> Review and edit every decision entry directly in the App.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Delete:</strong> Remove individual decisions at any time.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Revoke Permissions:</strong> Turn off notification permission at any time in iOS Settings; the App will continue to work without reminders.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Uninstall:</strong> Removing the App deletes all locally stored data permanently.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Verdict is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can take appropriate action.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/verdict/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    Verdict Support Page
                  </Link>
                </p>
              </div>
            </section>
      </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
